<template>
  <div
    class="fallback-cover"
    :class="textColorModifierClass"
    :style="{
      '--background-color': backgroundColor,
    }"
  >
    <div class="fallback-cover__inner">
      <p class="fallback-cover__title" :class="titleSizeModifierClass">
        {{ title }}
      </p>
      <p class="fallback-cover__subtitle">{{ subtitle }}</p>
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed } from 'vue'

const props = defineProps<{
  title: string
  subtitle: string
}>()

// Colors that have good contrast with white text.
const darkColors = [
  '#418bfb',
  '#fe2d2d',
  '#3f5ed2',
  '#6c3fc9',
  '#ee1964',
  '#ff6b17',
  '#212121',
  '#96a1b0',
  '#4d3727',
  '#1b2c4a',
  '#b0ad89',
  '#dea882',
]

// Colors that need dark text to have sufficient contrast.
const lightColors = [
  '#ffc906',
  '#0ed1e3',
  '#65de51',
  '#d3bdb7',
  '#c0dee2',
  '#e5cb9a',
  '#c4b5bc',
]

const colors = [...darkColors, ...lightColors]

/**
 * Get a color for the background of the book cover.
 *
 * The color is picked randomly from the `colors` array
 * using the book title as a seed, so that it stays the same
 * between renders.
 */
const backgroundColor = computed(() => {
  const lowerCaseTitle = props.title.toLowerCase()

  let index = 0
  for (let i = 0; i < lowerCaseTitle.length; i++) {
    index += lowerCaseTitle.charCodeAt(i)
  }
  index = index % colors.length

  return colors[index]
})

/**
 * Return the modifier class for the text color.
 *
 * If the background color is light, we use black text.
 * If the background color is dark, we use white text.
 *
 * This assures we have good contrast between text and background.
 */
const textColorModifierClass = lightColors.includes(backgroundColor.value)
  ? 'fallback-cover--dark-text'
  : 'fallback-cover--light-text'

/**
 * Return the corresponding modifier class for the book title.
 *
 * For titles with long words, return a class modifier that reduces
 * the scale of the title, so that the word doesn't have to break or be
 * cut off.
 */
const titleSizeModifierClass = computed((): string | undefined => {
  const wordsInTitle = props.title.split(' ')
  // Find the longest word in the array
  const longestWord = wordsInTitle.reduce((a, b) => {
    return a.length > b.length ? a : b
  })

  if (longestWord.length > 16) {
    return 'fallback-cover__title--tiny'
  } else if (longestWord.length > 13) {
    return 'fallback-cover__title--smaller'
  } else if (longestWord.length > 9) {
    return 'fallback-cover__title--small'
  }

  return undefined
})
</script>

<style lang="scss" scoped>
.fallback-cover {
  display: flex;
  align-items: center;
  width: var(--cover-width, 154px);
  aspect-ratio: 3/4;
  border-radius: 9% / 6.75%;
  background: var(--background-color);

  &__inner {
    display: grid;
    grid-template-rows: 4fr 1fr;
    width: 100%;
    height: 93%;
    padding: 8%;
    margin: 5%;
    border: calc(var(--cover-width, 154px) * 0.013) solid;
    border-radius: calc(var(--cover-width, 154px) * 0.065);
  }

  &__title,
  &__subtitle {
    overflow: hidden;
    text-overflow: ellipsis;
    margin: 0;
    line-height: 1.16;
    text-wrap: balance;
    text-align: center;
    user-select: none;
    display: -webkit-box;
    -webkit-box-orient: vertical;
  }

  &__title {
    --title-scale: 0.12;
    align-self: center;
    font-size: calc(var(--cover-width, 154px) * var(--title-scale));
    font-weight: 500;
    word-break: break-word;
    text-wrap: balance;
    margin-bottom: 10%;
    -webkit-line-clamp: 6;
    max-height: 6lh;

    &--small {
      --title-scale: 0.1;
      margin-bottom: 5%;
    }

    &--smaller {
      --title-scale: 0.085;
      margin-bottom: 3%;
    }

    &--tiny {
      --title-scale: 0.078;
      margin-bottom: 3%;
    }
  }

  &__subtitle {
    align-self: end;
    font-size: calc(var(--cover-width, 154px) * 0.068);
    font-weight: 500;
    -webkit-line-clamp: 2;
    max-height: 2lh;
    opacity: 0.8;
  }
}

.fallback-cover--light-text .fallback-cover {
  &__inner {
    border-color: rgba($c-white, 0.3);
  }

  &__title,
  &__subtitle {
    color: $c-white;
  }
}

.fallback-cover--dark-text .fallback-cover {
  &__inner {
    border-color: rgba($c-dark-1, 0.5);
  }

  &__title,
  &__subtitle {
    color: $c-dark-1;
  }
}
</style>
